import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLanguageFromPath, translatePath } from '@/lib/routes';

export type Language = 'en' | 'es';

export interface Translations {
  // Navigation
  home: string;
  about: string;
  services: string;
  smartPricing: string;
  consulting: string;
  research: string;
  blog: string;
  contact: string;
  
  // Hero Section
  heroTagline: string;
  heroSubtitle: string;
  heroCtaButton: string;
  freeDemo: string;
  
  // Benefits
  benefitsTitle: string;
  benefit1: string;
  benefit2: string;
  benefit3: string;
  
  // Lead Magnet
  leadMagnetTitle: string;
  leadMagnetDescription: string;
  downloadButton: string;
  
  // Form Labels
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  submit: string;
  
  // Common
  learnMore: string;
  learnMoreAbout: string;
  getStarted: string;
  requestDemo: string;
  
  // Page specific content
  comingSoon: string;
  readMore: string;
  viewAll: string;
  subscribe: string;
  bookConsultation: string;
  downloadGuide: string;
  searchPlaceholder: string;
  newsletter: string;
  stayUpdated: string;
  emailPlaceholder: string;
  
  // Service specific
  smartPricingTitle: string;
  smartPricingSubtitle: string;
  consultingTitle: string;
  consultingSubtitle: string;
  researchTitle: string;
  researchSubtitle: string;
  
  // Common service sections
  benefits: string;
  howItWorks: string;
  caseStudies: string;
  faq: string;
  bookDemo: string;
  downloadChecklist: string;
  requestQuote: string;
}

const translations: Record<Language, Translations> = {
  en: {
    // Navigation
    home: 'Home',
    about: 'About Us',
    services: 'Services',
    smartPricing: 'Smart PriceZing',
    consulting: 'Pricing Consulting',
    research: 'Market Research',
    blog: 'Blog',
    contact: 'Contact',
    
    // Hero Section
    heroTagline: 'Your prices tell a story. Make sure it\'s profitable.',
    heroSubtitle: 'Transform your pricing strategy with data-driven insights and expert consulting. Serving businesses across LATAM and the U.S.',
    heroCtaButton: 'Book Your Free Diagnostic Session',
    freeDemo: 'Request Free Demo',
    
    // Benefits
    benefitsTitle: 'Why Choose PriceZing?',
    benefit1: 'Stop losing money unknowingly',
    benefit2: 'React faster than competitors',
    benefit3: 'Build defendable pricing models',
    
    // Lead Magnet
    leadMagnetTitle: 'Profitable Pricing Strategy Checklist',
    leadMagnetDescription: 'Download our comprehensive guide to optimize your pricing strategy',
    downloadButton: 'Download Free Guide',
    
    // Form Labels
    name: 'Full Name',
    email: 'Email Address',
    company: 'Company',
    phone: 'Phone Number',
    message: 'Message',
    submit: 'Submit',
    
    // Common
    learnMore: 'Learn More',
    learnMoreAbout: 'Learn More About PriceZing',
    getStarted: 'Get Started',
    requestDemo: 'Request Demo',
    
    // Page specific content
    comingSoon: 'Coming Soon',
    readMore: 'Read More',
    viewAll: 'View All',
    subscribe: 'Subscribe',
    bookConsultation: 'Book Consultation',
    downloadGuide: 'Download Guide',
    searchPlaceholder: 'Search...',
    newsletter: 'Newsletter',
    stayUpdated: 'Stay Updated',
    emailPlaceholder: 'Your email address',
    
    // Service specific
    smartPricingTitle: 'Smart PriceZing',
    smartPricingSubtitle: 'Omnichannel price monitoring and alerting platform for ecommerce, retail, and distributors.',
    consultingTitle: 'Pricing Strategy Consulting',
    consultingSubtitle: 'We design profitable, defendable, value-based pricing strategies.',
    researchTitle: 'International Market Price Research',
    researchSubtitle: 'Real, defendable pricing research to compete and expand into new markets.',
    
    // Common service sections
    benefits: 'Benefits',
    howItWorks: 'How It Works',
    caseStudies: 'Case Studies',
    faq: 'Frequently Asked Questions',
    bookDemo: 'Book a demo',
    downloadChecklist: 'Download checklist',
    requestQuote: 'Request Quote',
  },
  es: {
    // Navigation
    home: 'Inicio',
    about: 'Nosotros',
    services: 'Servicios',
    smartPricing: 'Smart PriceZing',
    consulting: 'Consultoría en Precios',
    research: 'Investigación de Mercado',
    blog: 'Blog',
    contact: 'Contacto',
    
    // Hero Section
    heroTagline: 'Tus precios cuentan una historia. Asegúrate que sea rentable.',
    heroSubtitle: 'Transforma tu estrategia de precios con datos y consultoría experta. Atendemos empresas en LATAM y EE. UU.',
    heroCtaButton: 'Agenda tu diagnóstico gratuito',
    freeDemo: 'Solicitar Demo Gratuito',
    
    // Benefits
    benefitsTitle: '¿Por qué elegir PriceZing?',
    benefit1: 'Deja de perder dinero sin saberlo',
    benefit2: 'Reacciona más rápido que la competencia',
    benefit3: 'Construye modelos de precios defendibles',
    
    // Lead Magnet
    leadMagnetTitle: 'Lista de Verificación de Estrategia de Precios Rentable',
    leadMagnetDescription: 'Descarga nuestra guía completa para optimizar tu estrategia de precios',
    downloadButton: 'Descargar Guía Gratuita',
    
    // Form Labels
    name: 'Nombre Completo',
    email: 'Correo Electrónico',
    company: 'Empresa',
    phone: 'Teléfono',
    message: 'Mensaje',
    submit: 'Enviar',
    
    // Common
    learnMore: 'Conoce más',
    learnMoreAbout: 'Conoce Más Sobre PriceZing',
    getStarted: 'Comenzar',
    requestDemo: 'Solicitar Demo',
    
    // Page specific content
    comingSoon: 'Próximamente',
    readMore: 'Leer Más',
    viewAll: 'Ver Todo',
    subscribe: 'Suscribirse',
    bookConsultation: 'Reservar Consulta',
    downloadGuide: 'Descargar Guía',
    searchPlaceholder: 'Buscar...',
    newsletter: 'Newsletter',
    stayUpdated: 'Mantente Actualizado',
    emailPlaceholder: 'Tu dirección de email',
    
    // Service specific
    smartPricingTitle: 'Smart PriceZing',
    smartPricingSubtitle: 'Plataforma de monitoreo y alertas de precios omnicanal para ecommerce, retail y distribuidores.',
    consultingTitle: 'Consultoría en Estrategia de Precios',
    consultingSubtitle: 'Diseñamos estrategias de precios rentables, defendibles y basadas en valor.',
    researchTitle: 'Investigación de Precios de Mercado Internacional',
    researchSubtitle: 'Estudios reales y defendibles para competir y expandir en nuevos mercados.',
    
    // Common service sections
    benefits: 'Beneficios',
    howItWorks: 'Cómo Funciona',
    caseStudies: 'Casos de Estudio',
    faq: 'Preguntas Frecuentes',
    bookDemo: 'Agenda tu demo',
    downloadChecklist: 'Descargar checklist',
    requestQuote: 'Solicitar Cotización',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState<Language>(() => {
    // Initialize from URL
    return getLanguageFromPath(location.pathname);
  });

  // Update language when URL changes
  useEffect(() => {
    const urlLanguage = getLanguageFromPath(location.pathname);
    if (urlLanguage !== language) {
      setLanguage(urlLanguage);
    }
  }, [location.pathname]);

  // Handle language change
  const handleSetLanguage = (newLanguage: Language) => {
    if (newLanguage !== language) {
      const newPath = translatePath(location.pathname, newLanguage);
      navigate(newPath);
      setLanguage(newLanguage);
    }
  };

  const value = {
    language,
    setLanguage: handleSetLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};